// Week 2: full app with React Router + Shell + Overview.
// Auth gate still lives here — only signed-in verified users reach the router.
// LoginScreen / VerifyScreen remain unchanged from Week 1.
// Portal (/portal?token=...) short-circuits BEFORE the auth gate — it's
// the client-facing unauthenticated view.

import { lazy, Suspense, useState } from 'react'
import { useAuth } from './hooks/useAuth'
import { signIn, signOut, sendEmailVerification, reloadUser } from './lib/firebase/auth'
import { captureErr, breadcrumb } from './lib/sentry'
import Router from './router'
import Toasts from './components/Toasts'

// Portal is the CLIENT-FACING page at /portal?token=X. It is unauthenticated
// by design — clients receive the link via email and never sign in. Must
// short-circuit BEFORE the auth gate below or the login screen hijacks them.
const Portal = lazy(() => import('./routes/Portal'))

function isPortalPath(): boolean {
  try {
    const p = window.location.pathname
    return p === '/portal' || p.startsWith('/portal/')
  } catch { return false }
}

export default function App() {
  const { user, loading } = useAuth()

  // Short-circuit for the client portal — render Portal directly, no auth,
  // no Shell, no login wall. Firestore rules already allow public reads on
  // shared/portallink__* so Portal.tsx can fetch without a signed-in user.
  if (isPortalPath()) {
    return (
      <>
        <Suspense fallback={<Splash>Loading portal…</Splash>}>
          <Portal />
        </Suspense>
        <Toasts />
      </>
    )
  }

  // Toasts are mounted at the root so they render outside the auth gate and
  // are available to every route (including portal views).
  const body =
    loading ? <Splash>Loading…</Splash>
    : !user ? <LoginScreen />
    : !user.emailVerified ? <VerifyScreen />
    : <Router />

  return (
    <>
      {body}
      <Toasts />
    </>
  )
}

// ── Splash ────────────────────────────────────────────────────────────────────

function Splash({ children }: { children: React.ReactNode }) {
  return (
    <div style={shellStyle}>
      <p style={{ color: '#94a3b8' }}>{children}</p>
    </div>
  )
}

// ── Login ─────────────────────────────────────────────────────────────────────

function LoginScreen() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setErr(null)
    setBusy(true)
    try {
      breadcrumb('auth', 'login submit', { email })
      await signIn(email.trim(), password)
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e)
      setErr(msg)
      captureErr(e, { where: 'LoginScreen.signIn' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={shellStyle}>
      <h1 style={h1Style}>JobSite Pulse</h1>
      <p style={{ color: '#94a3b8', marginBottom: '1.5rem' }}>Sign in to continue</p>

      <form onSubmit={onSubmit} style={cardStyle}>
        <label style={labelStyle}>
          <span>Email</span>
          <input
            type="email" required autoComplete="email"
            value={email} onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
          />
        </label>
        <label style={labelStyle}>
          <span>Password</span>
          <input
            type="password" required autoComplete="current-password"
            value={password} onChange={(e) => setPassword(e.target.value)}
            style={inputStyle}
          />
        </label>
        {err && <div style={errStyle}>{err}</div>}
        <button type="submit" disabled={busy} style={btnStyle}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  )
}

// ── Verify email ──────────────────────────────────────────────────────────────

function VerifyScreen() {
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)

  async function onResend() {
    setBusy(true)
    try {
      await sendEmailVerification()
      setSent(true)
    } catch (e) {
      captureErr(e, { where: 'VerifyScreen.sendEmailVerification' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={shellStyle}>
      <h1 style={h1Style}>Verify your email</h1>
      <p style={{ color: '#94a3b8', marginBottom: '1.5rem', textAlign: 'center', maxWidth: '28rem' }}>
        We sent a verification link. Click it, then come back here and refresh.
      </p>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button onClick={() => reloadUser().then(() => location.reload())} style={btnStyle}>
          I verified — refresh
        </button>
        <button onClick={onResend} disabled={busy || sent} style={{ ...btnStyle, background: '#334155' }}>
          {sent ? 'Sent' : busy ? 'Sending…' : 'Resend'}
        </button>
      </div>
      <button onClick={() => signOut()} style={{ ...btnStyle, marginTop: '1rem', background: 'transparent', color: '#94a3b8', boxShadow: 'none' }}>
        Sign out
      </button>
    </div>
  )
}

// ── Inline styles (pre-app-shell only — once Router renders, CSS vars take over) ──

const shellStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif',
  background: '#0f172a', color: '#f8fafc', padding: '2rem', gap: '0.5rem',
}
const h1Style: React.CSSProperties = { fontSize: '1.875rem', fontWeight: 700, margin: 0, letterSpacing: '-0.02em' }
const cardStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: '0.75rem',
  padding: '1.5rem', background: '#1e293b', borderRadius: '0.75rem', maxWidth: '24rem', width: '100%',
}
const labelStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.875rem', color: '#94a3b8' }
const inputStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem', borderRadius: '0.375rem', border: '1px solid #334155',
  background: '#0f172a', color: '#f8fafc', fontSize: '0.875rem',
}
const btnStyle: React.CSSProperties = {
  padding: '0.5rem 1rem', borderRadius: '0.5rem', border: 'none',
  background: '#22c55e', color: '#0f172a', fontSize: '0.875rem', fontWeight: 700, cursor: 'pointer',
  transition: 'background 0.15s',
}
const errStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem', background: '#7f1d1d', color: '#fecaca', borderRadius: '0.375rem', fontSize: '0.8rem',
}
